/** @format */

import { Brand } from "@question-bank/shared";
import { withCache, resolveCacheKey, referenceCacheKey } from "./coreTaxonomyCache";

/**
 * Client for the Core Taxonomy API (QB2-309), built to the contract in
 * `docs/core-taxonomy-api-contract.md` (QB2-268).
 *
 * The Core API resolves brand from the request host — each brand has its own
 * custom domain (e.g. `api.core.dev.resources.wjec.co.uk` vs the Eduqas host).
 * The base URL is therefore brand-scoped, mirroring the question API client in
 * `questions.ts`. Reads are public; `resolve` is a public read too, so plain
 * `fetch` is used throughout.
 */

/** Brand-scoped Core Taxonomy API base URL, trailing slash stripped. */
export const getCoreApiBaseUrl = (brand: Brand): string => {
	const baseUrls: Record<Brand, string> = {
		wjec: import.meta.env.VITE_CORE_WJEC_API_URL || "/api/core/wjec",
		eduqas: import.meta.env.VITE_CORE_EDUQAS_API_URL || "/api/core/eduqas",
	};
	return baseUrls[brand].replace(/\/+$/, "");
};

const JSON_HEADERS: HeadersInit = {
	"Content-Type": "application/json",
};

// ─── Contract types (mirror the Core API shapes named in the contract) ──────

/** Bilingual text as returned by the Core API. `cy` is present for WJEC only. */
export interface LocaleText {
	en: string;
	cy?: string;
}

/** Specification-item lifecycle (Core API ADR 0002). */
export type SpecItemStatus = "current" | "superseded";

/** Specification-edition lifecycle (Core API ADR 0002). */
export type SpecEditionStatus = "draft" | "current" | "superseded";

/** One edition of a specification for a subject and qualification. */
export interface SpecEdition {
	uuid: string;
	subject: string;
	qualification: string;
	label: string;
	status: SpecEditionStatus;
}

export interface SpecEditionList {
	subject: string;
	qualification: string;
	data: SpecEdition[];
}

/** A single resolve candidate: the stable identity plus a human-readable path. */
export interface ResolveCandidate {
	/** The stable identity a question pins to (survives edition change). */
	lineageId: string;
	/** The per-edition item id the candidate came from. */
	uuid: string;
	status: SpecItemStatus;
	path: {
		theme: LocaleText;
		topic: LocaleText;
		subtopic: LocaleText;
	};
}

/** Request body for the resolve endpoint. `version` is the edition label. */
export interface ResolveRequest {
	version: string;
	theme: string;
	topic: string;
	subtopic: string;
}

/**
 * Resolve response. `candidates` is ordered most-confident first and may hold
 * zero, one, or many entries — all three are `200` outcomes the caller handles
 * (see the contract), not errors.
 */
export interface ResolveResponse {
	subject: string;
	qualification: string;
	version: string;
	editionId: string;
	candidates: ResolveCandidate[];
}

/** RFC 9457 problem+json body, as the Core API returns for error cases. */
export interface Problem {
	type?: string;
	title: string;
	status: number;
	detail?: string;
	instance?: string;
}

/**
 * Error thrown when the Core API answers with a problem+json body (or any
 * non-OK response). Carries the HTTP status so a caller can tell a
 * version-not-found (`404`) from a draft-edition refusal (`409`) or an
 * outage (`5xx`), which the contract requires it to distinguish.
 */
export class CoreTaxonomyError extends Error {
	readonly status: number;
	readonly problem?: Problem;

	constructor(status: number, message: string, problem?: Problem) {
		super(message);
		this.name = "CoreTaxonomyError";
		this.status = status;
		this.problem = problem;
	}
}

/** Build a {@link CoreTaxonomyError} from a failed response, reading problem+json when present. */
const buildCoreError = async (response: Response, fallback: string): Promise<CoreTaxonomyError> => {
	const problem = (await response.json().catch(() => null)) as Problem | null;
	const message = problem?.detail || problem?.title || `${fallback}: ${response.statusText}`;
	return new CoreTaxonomyError(response.status, message, problem ?? undefined);
};

// ─── Client functions ───────────────────────────────────────────────────────

/**
 * Resolve a free-text taxonomy path against a specification version.
 *
 * Returns 0..n candidates. The caller decides what to do by count: one
 * auto-assigns, zero holds the row for a human, two or more surface a dropdown
 * (see the contract). A `404` (unknown version, subject, or qualification),
 * `409` (draft edition), `400` (malformed) or `5xx` (unavailable) throws a
 * {@link CoreTaxonomyError} carrying the status.
 *
 * The result is cached (QB2-290) on brand + subject + qualification + version +
 * normalised statement text — never on a sub-topic code. Repeated lookups for
 * the same statement are served from cache. A failed lookup is not cached.
 */
export const resolveSubtopic = (
	brand: Brand,
	subject: string,
	qualification: string,
	request: ResolveRequest,
): Promise<ResolveResponse> =>
	withCache(resolveCacheKey(brand, subject, qualification, request), () =>
		resolveSubtopicUncached(brand, subject, qualification, request),
	);

/** The raw resolve transport, without caching. Prefer {@link resolveSubtopic}. */
const resolveSubtopicUncached = async (
	brand: Brand,
	subject: string,
	qualification: string,
	request: ResolveRequest,
): Promise<ResolveResponse> => {
	const baseUrl = getCoreApiBaseUrl(brand);
	const url = `${baseUrl}/specifications/${encodeURIComponent(subject)}/${encodeURIComponent(qualification)}/resolve`;

	const response = await fetch(url, {
		method: "POST",
		headers: JSON_HEADERS,
		body: JSON.stringify(request),
	});

	if (!response.ok) {
		throw await buildCoreError(response, "Failed to resolve subtopic");
	}

	return response.json();
};

/**
 * List the editions for a subject and qualification, oldest first. Used to
 * validate that an import's specification version exists and to resolve its
 * label to an `editionId` before resolving rows. Cached per brand + path
 * (QB2-290).
 */
export const listEditions = (brand: Brand, subject: string, qualification: string): Promise<SpecEditionList> => {
	const path = `/specifications/${encodeURIComponent(subject)}/${encodeURIComponent(qualification)}/editions`;
	return withCache(referenceCacheKey(brand, path), () =>
		getJson<SpecEditionList>(brand, path, "Failed to list specification editions"),
	);
};

// ─── Reference-data reads (QB2-224) ─────────────────────────────────────────

/**
 * A reference-data item shared by tiers, series, and assessment objectives:
 * a stable id, a bilingual title, and a display order. The id is the value a
 * question stores in `coreMappings`; the title is for display only.
 */
export interface ReferenceItem {
	id: string;
	title: LocaleText;
	order: number;
}

/** List envelope used by the Core API reference-data reads. */
export interface ReferenceList {
	data: ReferenceItem[];
}

/** A Core API subject. Only the fields a consumer needs are typed. */
export interface Subject {
	id: string;
	title: LocaleText;
	indexed?: boolean;
	category?: string[];
	icon?: string;
}

export interface SubjectList {
	data: Subject[];
	nextCursor?: string;
}

/**
 * A Core API qualification (QB2-223). A first-class taxonomy entity with its
 * own `code` (the stable key used in specification paths, e.g. `alevel`) and
 * `uuid`. Richer than the old question `level` enum — a paper stores the
 * `code` (and optionally `uuid`) rather than a GCSE/AS/A-Level string.
 */
export interface Qualification {
	uuid: string;
	code: string;
	title: LocaleText;
	group?: string;
	keyStage?: string[];
	indexed?: boolean;
}

export interface QualificationList {
	data: Qualification[];
	nextCursor?: string;
}

/** GET and parse a Core API JSON read for the resolved brand. Uncached transport. */
const getJson = async <T>(brand: Brand, path: string, fallback: string): Promise<T> => {
	const baseUrl = getCoreApiBaseUrl(brand);
	const response = await fetch(`${baseUrl}${path}`, {
		method: "GET",
		headers: JSON_HEADERS,
	});

	if (!response.ok) {
		throw await buildCoreError(response, fallback);
	}

	return response.json();
};

/**
 * Fetch and parse a Core API list read for the resolved brand, cached per
 * brand + path (QB2-290). Reference data is read repeatedly to resolve the
 * ids stored on a question; the cache serves a repeat without a network call.
 */
const getReferenceList = <T>(brand: Brand, path: string): Promise<T> =>
	withCache(referenceCacheKey(brand, path), () => getJson<T>(brand, path, `Failed to list ${path}`));

/** List assessment tiers (Foundation/Intermediate/Higher) in display order. */
export const listTiers = (brand: Brand): Promise<ReferenceList> => getReferenceList<ReferenceList>(brand, "/tiers");

/** List assessment series (seasons) in display order. */
export const listSeries = (brand: Brand): Promise<ReferenceList> => getReferenceList<ReferenceList>(brand, "/series");

/** List assessment objectives (AO1–AO3) in display order. */
export const listAssessmentObjectives = (brand: Brand): Promise<ReferenceList> =>
	getReferenceList<ReferenceList>(brand, "/assessment-objectives");

/** List subject categories (WJEC subject groups) in display order. */
export const listSubjectCategories = (brand: Brand): Promise<ReferenceList> =>
	getReferenceList<ReferenceList>(brand, "/subject-categories");

/**
 * List subjects for the resolved brand. Subjects carry more than the shared
 * reference shape (indexed/category/icon), so they have their own type — but
 * the transport is the same generic list read.
 */
export const listSubjects = (brand: Brand): Promise<SubjectList> => getReferenceList<SubjectList>(brand, "/subjects");

/**
 * List qualifications for the resolved brand (QB2-223). Brand-scoped
 * `GET /qualifications`. Qualifications carry a `code`/`uuid` and bilingual
 * title, so they have their own type; the transport is the same cached read.
 */
export const listQualifications = (brand: Brand): Promise<QualificationList> =>
	getReferenceList<QualificationList>(brand, "/qualifications");
